/**
 * The port between the focus core and what draws it. Commands write the
 * presentation store first and then let the presentation's renderer apply
 * it; when a presentation ends, the renderer restores first and the store
 * is written last.
 */
import { canvasFocusAdapter } from "@/components/canvas/canvas-focus-adapter";
import { overlayFocusAdapter } from "@/lib/focus/overlay-focus-adapter";
import type {
  FocusRendererKind,
  PresentationState,
} from "@/lib/focus/focus-types";
import { usePresentationStore } from "@/stores/presentation-store";

export interface FocusApplyOptions {
  /** Layout transition; the renderer's default when omitted. */
  duration?: number;
  /** The presentation starts with this apply. */
  entering?: boolean;
  /** A reflow: the renderer skips it when it already shows `next`. */
  reflow?: boolean;
}

export interface FocusRestoreOptions {
  restoreViewport: boolean;
  /** Another presentation replaces this one and keeps its restore point. */
  replaced?: boolean;
}

export interface FocusAdapter {
  apply(
    prev: PresentationState | null,
    next: PresentationState,
    options: FocusApplyOptions,
  ): void;
  restore(prev: PresentationState, options: FocusRestoreOptions): void;
  revealOnWorkspace(frameId: string): void;
}

const FOCUS_ADAPTERS: Record<FocusRendererKind, FocusAdapter> = {
  canvas: canvasFocusAdapter,
  overlay: overlayFocusAdapter,
};

export function focusAdapterFor(kind: FocusRendererKind): FocusAdapter {
  return FOCUS_ADAPTERS[kind];
}

/** Write `next`, then let its renderer draw the change from `prev`. */
export function commitPresentation(
  next: PresentationState,
  options: FocusApplyOptions = {},
  prev: PresentationState | null = usePresentationStore.getState().presentation,
): void {
  usePresentationStore.setState({ presentation: next });
  focusAdapterFor(next.renderer).apply(
    prev?.renderer === next.renderer ? prev : null,
    next,
    options,
  );
}
